import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import type { Locale } from "../../../shared/types";
import type {
  ArtifactKind,
  EvidenceUnit,
  SaturationReport,
  StudySummary,
  SynthesisState,
  ThemeReport,
} from "../lib/api";
import * as api from "../lib/api";
import { formatSince, useCopy } from "../lib/copy";
import type { Copy } from "../lib/copy";
import type { StudyShell } from "./Studies";

/**
 * The payoff screen.
 *
 * Everything the category gets wrong is a rule here. Every theme carries its
 * respondent count AND its denominator, because "18% of nine people" is a lie and
 * "7 of 11" is not. Contradictions sit beside the evidence rather than averaged into
 * it, because the disagreement is where the work is. Directness is marked, so a theme
 * built on "I would definitely pay for that" cannot pass for one built on what
 * somebody actually did. A claim one person made is in its own section, named as
 * such, and never counted.
 *
 * A run costs real money, so it happens on a press and never on its own. While it
 * runs there is no spinner and no progress bar: the blob thinks and the status line
 * says so in words.
 */

interface SynthesisProps {
  locale: Locale;
  studyId: number;
  study: StudySummary;
  /** Finished interviews. With none there is nothing to offer a button for. */
  completed: number;
  /** The server's own judgement on whether a run is worth the money yet. */
  ready: boolean;
  shell: StudyShell;
}

/** How often a running job is asked where it has got to. */
const POLL_MS = 3000;

/** All five downloads, in the order a person wants them. */
const ARTIFACTS: { kind: ArtifactKind; key: "brief" | "docx" | "csv" | "book" | "page" }[] = [
  { kind: "brief.md", key: "brief" },
  { kind: "brief.docx", key: "docx" },
  { kind: "evidence.csv", key: "csv" },
  { kind: "evidence.xlsx", key: "book" },
  { kind: "study.html", key: "page" },
];

export default function Synthesis({
  locale,
  studyId,
  study,
  completed,
  ready,
  shell,
}: SynthesisProps) {
  const copy = useCopy(locale);
  const [state, setState] = useState<SynthesisState | null>(null);
  const [expanded, setExpanded] = useState<number[]>([]);
  const { setBusy, say } = shell;

  /** Whether this component is currently holding the status line. */
  const heldRef = useRef(false);
  /**
   * True for the report that finished while you were watching, and only that one.
   *
   * A synthesis is the longest wait in the product and the only screen a founder
   * paid for by pressing a button. Arriving is worth being a moment: the answer
   * settles first, then the line under it, then the themes. A report that was
   * already there when the board opened does not animate at all — the same rule the
   * participant rows follow, and the reason the entrance means something when it
   * does happen.
   */
  const [arrived, setArrived] = useState(false);

  const read = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const next = await api.getSynthesis(studyId, signal);
        if (signal?.aborted !== true) setState(next);
      } catch (err) {
        if (signal?.aborted === true) return;
        say(api.describe(err, copy.synthesisFailed), "error");
      }
    },
    [copy.synthesisFailed, say, studyId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void read(controller.signal);
    return () => controller.abort();
  }, [read]);

  const running = state !== null && state.running;

  // A run in flight is the longest wait in the product. It is polled, the blob thinks
  // for the whole of it, and the status line says what is happening. The server's own
  // progress text is English prose, so this writes its own line instead: at the one
  // place the product's credibility sits, a Russian reader does not get English.
  useEffect(() => {
    if (!running) {
      if (heldRef.current) {
        heldRef.current = false;
        setBusy(null);
        // It was running a moment ago and it is not now: this is the arrival.
        setArrived(true);
      }
      return;
    }
    heldRef.current = true;
    setBusy(copy.synthesisRunningVague);
    const timer = window.setInterval(() => void read(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [copy.synthesisRunningVague, read, running, setBusy]);

  // Leaving the board mid-run must not leave the status line describing a screen that
  // is no longer on it.
  useEffect(
    () => () => {
      if (heldRef.current) {
        heldRef.current = false;
        setBusy(null);
      }
    },
    [setBusy],
  );

  const run = useCallback((): void => {
    setState((current) =>
      current === null
        ? { running: true, status: "", startedAt: null, run: null }
        : { ...current, running: true },
    );
    void (async () => {
      try {
        // A run that already covers every completed interview comes straight back
        // rather than being paid for a second time.
        setState(await api.synthesize(studyId));
      } catch (err) {
        setState((current) => (current === null ? null : { ...current, running: false }));
        say(api.describe(err, copy.synthesisFailed), "error");
      }
    })();
  }, [copy.synthesisFailed, say, studyId]);

  const toggle = useCallback((id: number): void => {
    setExpanded((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  }, []);

  const report = state?.run ?? null;
  // A theme below the study's reporting threshold is not shown at all, and the count
  // of what is missing is. An employee study that quietly dropped three themes would
  // be a worse promise than one that says it dropped them.
  const shown = report === null ? [] : report.themes.filter((theme) => !theme.withheld);
  const withheld = report === null ? 0 : report.themes.length - shown.length;
  // Themes the server disqualified for priming. They arrive on the wire beside
  // `themes`; the declaration belongs in web/src/lib/api.ts, which another hand owns
  // tonight, so this reads the field structurally rather than editing that contract.
  // Optional on purpose: an older server simply sends nothing and the section is not
  // rendered.
  const uncounted: ThemeReport[] =
    (report as { uncounted?: ThemeReport[] } | null)?.uncounted ?? [];

  return (
    <section className={arrived ? "synth synth--arrived" : "synth"}>
      <h3 className="synth__header label">{copy.synthesisLabel}</h3>

      {report === null ? (
        <p className="synth__none">{copy.synthesisNone}</p>
      ) : (
        <>
          <p className="synth__from label">
            {copy.synthesisFrom(report.nInterviews, formatSince(report.createdAt, locale))}
          </p>

          <p className="synth__answer-label label">{copy.synthesisAnswerLabel}</p>
          <p className="synth__answer">{report.answer.join(" ")}</p>

          <p className="synth__saturation">{saturationLine(report, copy)}</p>

          <h4 className="synth__section label">{copy.synthesisThemesLabel}</h4>
          {withheld > 0 && <p className="synth__hint">{copy.synthesisWithheld(withheld)}</p>}
          <ul className="themes">
            {shown.map((theme, index) => (
              <ThemeRow
                key={theme.id}
                theme={theme}
                locale={locale}
                index={index}
                open={expanded.includes(theme.id)}
                onToggle={() => toggle(theme.id)}
              />
            ))}
          </ul>

          {/* Themes made of the interviewer's own words, kept and named rather than
              quietly deleted. They carry no count: the count is the part that misleads,
              because "4 of 4" describes how often the question was asked. */}
          {uncounted.length > 0 && (
            <>
              <h4 className="synth__section label">{copy.synthesisUncountedLabel}</h4>
              <p className="synth__hint">{copy.synthesisUncountedHint}</p>
              <ul className="themes">
                {uncounted.map((theme, index) => (
                  <ThemeRow
                    key={theme.id}
                    theme={theme}
                    locale={locale}
                    index={index}
                    counted={false}
                    open={expanded.includes(theme.id)}
                    onToggle={() => toggle(theme.id)}
                  />
                ))}
              </ul>
            </>
          )}

          {/* The say-do gap, caught mechanically. Nobody else in the category produces
              this, which is exactly why it gets a section of its own. */}
          {report.sayDid.length > 0 && (
            <>
              <h4 className="synth__section label">{copy.synthesisSaidDidLabel}</h4>
              <p className="synth__hint">{copy.synthesisSaidDidHint}</p>
              <ul className="saiddid">
                {report.sayDid.map((pair, index) => (
                  <li className="saiddid__item" key={`${pair.participantId}-${index}`}>
                    <p className="saiddid__who label">{pair.speaker}</p>
                    <p className="saiddid__side label">{copy.synthesisSaid}</p>
                    <blockquote className="quote__text">{pair.said.quote}</blockquote>
                    <p className="saiddid__side label">{copy.synthesisDid}</p>
                    <blockquote className="quote__text">{pair.did.quote}</blockquote>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Where respondents split, kept unresolved and shown from both sides. */}
          {report.disagreements.length > 0 && (
            <>
              <h4 className="synth__section label">{copy.synthesisDisagreementsLabel}</h4>
              <p className="synth__hint">{copy.synthesisDisagreementsHint}</p>
              <ul className="saiddid">
                {report.disagreements.map((split) => (
                  <li className="saiddid__item" key={split.codeId}>
                    <p className="saiddid__who label">{split.code}</p>
                    <p className="saiddid__side label">{copy.synthesisFor}</p>
                    <ul className="quotes">
                      {split.supports.map((unit, index) => (
                        <QuoteRow key={unit.id} unit={unit} locale={locale} index={index} />
                      ))}
                    </ul>
                    <p className="saiddid__side label">{copy.synthesisAgainst}</p>
                    <ul className="quotes">
                      {split.contradicts.map((unit, index) => (
                        <QuoteRow key={unit.id} unit={unit} locale={locale} index={index} />
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </>
          )}

          {report.singletons.length > 0 && (
            <>
              <h4 className="synth__section label">{copy.synthesisSinglesLabel}</h4>
              <p className="synth__hint">{copy.synthesisSinglesHint}</p>
              <ul className="quotes">
                {report.singletons.map((single, index) => (
                  <QuoteRow
                    key={single.codeId}
                    unit={single.quote}
                    locale={locale}
                    index={index}
                  />
                ))}
              </ul>
            </>
          )}

          {report.hypotheses.length > 0 && (
            <>
              <h4 className="synth__section label">{copy.synthesisHypothesesLabel}</h4>
              <ul className="verdicts">
                {report.hypotheses.map((item, index) => (
                  <li className="verdict" key={index}>
                    <span className="verdict__word label">
                      {copy.synthesisVerdict[item.verdict]}
                    </span>
                    <p className="verdict__text">{item.hypothesis}</p>
                    {item.note.length > 0 && <p className="verdict__note">{item.note}</p>}
                  </li>
                ))}
              </ul>
            </>
          )}

          {report.nextQuestions.length > 0 && (
            <>
              <h4 className="synth__section label">{copy.synthesisNextLabel}</h4>
              <ul className="nexts">
                {report.nextQuestions.map((question, index) => (
                  <li className="next" key={index}>
                    {question}
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="artifacts">
            <h4 className="synth__section label">{copy.synthesisArtifactsLabel}</h4>
            {ARTIFACTS.map((file) => (
              <a
                className="artifact"
                key={file.kind}
                href={api.artifactUrl(studyId, file.kind, locale)}
              >
                {copy.synthesisArtifact[file.key]}
              </a>
            ))}
            <p className="artifacts__sub">{copy.synthesisArtifactsSub}</p>
          </div>
        </>
      )}

      {/* One button, and it is never automatic. The sentence under it says what a
          press costs, because nothing here may imply the run is free. */}
      {completed > 0 && (
        <div className="synth__run">
          <button
            className={ready || report !== null ? "btn btn--primary" : "btn btn--quiet"}
            type="button"
            disabled={running}
            onClick={run}
          >
            {report === null ? copy.synthesisRunCta : copy.synthesisRunAgainCta}
          </button>
          <p className="synth__cost">{copy.synthesisCost}</p>
          {study.audience === "employee" && study.minReportN > 1 && (
            <p className="synth__cost">{copy.synthesisThreshold(study.minReportN)}</p>
          )}
        </div>
      )}
    </section>
  );
}

// --- one theme -------------------------------------------------------------

interface ThemeRowProps {
  theme: ThemeReport;
  locale: Locale;
  /** Position in its own list, for the arrival stagger. Never a layout input. */
  index: number;
  /**
   * False for a theme the priming check disqualified. It keeps its statement and its
   * quotes and loses its numbers — a count on a theme built out of the interviewer's
   * question is the one part of it that would be believed.
   */
  counted?: boolean;
  open: boolean;
  onToggle: () => void;
}

/**
 * A theme is a claim, not a topic, so the falsifiable sentence gets the serif and the
 * noun-phrase title is demoted to a mono label above it. The count sits under both,
 * in the mono face, with its denominator spelled out — because the denominator is the
 * whole argument: somebody who was never asked about pricing is not evidence against
 * a pricing theme.
 *
 * Expanding does not animate the box. Layout is never animated; what animates is the
 * words arriving inside it, the same entrance the notes rail uses.
 */
function ThemeRow({ theme, locale, index, counted = true, open, onToggle }: ThemeRowProps) {
  const copy = useCopy(locale);
  const evidence = theme.quotes.length + theme.contradictions.length;
  return (
    <li className="theme" style={{ ["--i"]: index } as CSSProperties}>
      <p className="theme__title label">{theme.title}</p>
      <p className="theme__statement">{theme.statement}</p>

      <p className="theme__counts">
        {counted ? (
          <>
            <span className="theme__count num">{copy.ofN(theme.supportN, theme.askedN)}</span>
            <span className="theme__asked label">{copy.synthesisAskedOf}</span>
            <span className="theme__band label">{copy.synthesisBand[theme.band]}</span>
            {theme.contraN > 0 && (
              <span className="theme__against label">
                {copy.synthesisAgainst} <span className="num">{theme.contraN}</span>
              </span>
            )}
          </>
        ) : (
          <span className="theme__band label">{copy.synthesisUncountedMark}</span>
        )}
      </p>

      {evidence > 0 && (
        <button
          className="theme__open label"
          type="button"
          aria-expanded={open}
          onClick={onToggle}
        >
          {open ? copy.synthesisEvidenceHideCta : copy.synthesisEvidenceCta}
        </button>
      )}

      {open && (
        <>
          <ul className="quotes">
            {theme.quotes.map((unit, index) => (
              <QuoteRow key={unit.id} unit={unit} locale={locale} index={index} />
            ))}
          </ul>

          {/* Kept, never averaged away. The contradiction is the interesting half. */}
          {theme.contradictions.length > 0 && (
            <>
              {/* One place behind the last supporting quote, so the expanded theme
                  reads down the page once. `--i` is set here rather than inherited:
                  the row above it carries the theme's own index. */}
              <p
                className="theme__against-header label"
                style={{ ["--i"]: theme.quotes.length } as CSSProperties}
              >
                {copy.synthesisAgainst}
              </p>
              <ul className="quotes">
                {theme.contradictions.map((unit, index) => (
                  <QuoteRow
                    key={unit.id}
                    unit={unit}
                    locale={locale}
                    index={theme.quotes.length + index}
                  />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </li>
  );
}

// --- one quote -------------------------------------------------------------

interface QuoteRowProps {
  unit: EvidenceUnit;
  locale: Locale;
  index: number;
}

/**
 * The evidence unit. Verbatim, never a paraphrase, with who said it, how they came to
 * say it, and whether the interviewer handed them the word first. A theme built on
 * primed evidence is a theme we put in the respondent's mouth, so it says so.
 *
 * `speaker` is already "Dana" or "P3" depending on the study's anonymity promise, and
 * this screen does not get to second-guess which.
 */
function QuoteRow({ unit, locale, index }: QuoteRowProps) {
  const copy = useCopy(locale);
  return (
    <li className="quote" style={{ ["--i"]: index } as CSSProperties}>
      <p className="quote__who label">
        {unit.speaker}
        {unit.segment !== null && unit.segment.length > 0 && (
          <>
            <span aria-hidden="true"> · </span>
            {unit.segment}
          </>
        )}
        <span aria-hidden="true"> · </span>
        {copy.synthesisDirectness[unit.directness]}
        {unit.primed && (
          <>
            <span aria-hidden="true"> · </span>
            {copy.synthesisPrimed}
          </>
        )}
      </p>
      <blockquote className="quote__text">{unit.quote}</blockquote>
    </li>
  );
}

// --- saturation ------------------------------------------------------------

/**
 * Guest, Namey and Chen (2020) in one sentence, never as a green tick.
 *
 * The server ships a finished `statement` and it is in English. This writes the same
 * sentence from the numbers beside it instead, so the Russian build does not fall
 * back to English at the one line a researcher will quote in a meeting.
 */
function saturationLine(
  report: { nInterviews: number; saturation: SaturationReport },
  copy: Copy,
): string {
  const { tooFew, saturatedAtN, zeroAtN, newInfoByRun } = report.saturation;
  if (tooFew) return copy.synthesisSaturationEarly(report.nInterviews);
  if (zeroAtN !== null) return copy.synthesisSaturationFirm(zeroAtN);
  if (saturatedAtN === null) return copy.synthesisSaturationNone;
  const run = newInfoByRun.find((entry) => entry.throughN === saturatedAtN);
  return copy.synthesisSaturationAt(saturatedAtN, Math.round(run?.newInfoPct ?? 0));
}
